// apps/web/src/pages/NotesPage.tsx
// 我的笔记 — Phase 4 实现

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listNotesApi, updateNoteApi, deleteNoteApi } from "@api/userData";
import type { NoteDTO } from "@examfast/shared-types";

export default function NotesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bankId = searchParams.get("bankId") ?? undefined;
  const [editingNote, setEditingNote] = useState<NoteDTO | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["notes", bankId],
    queryFn: () => listNotesApi({ page_size: 50, bank_id: bankId }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      updateNoteApi(id, content),
    onSuccess: () => {
      toast.success("笔记已更新");
      setEditingNote(null);
      queryClient.invalidateQueries({ queryKey: ["notes"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteNoteApi,
    onSuccess: () => {
      toast.success("笔记已删除");
      queryClient.invalidateQueries({ queryKey: ["notes"] });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white p-4 space-y-5">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-3 border-b border-gray-100 pb-5">
            <div className="h-5 w-40 rounded bg-gray-100 animate-pulse" />
            <div className="h-7 w-48 rounded bg-gray-100 animate-pulse" />
            <div className="h-20 rounded-xl bg-gray-100 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">加载失败，请重试</div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="min-h-screen bg-white pb-8">
      <NotesHeader title={bankId ? "题库笔记" : "我的笔记"} onBack={() => navigate(-1)} />

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-base text-gray-500">暂无笔记</p>
          <p className="text-sm mt-2">在题目中添加的笔记会显示在这里</p>
        </div>
      ) : (
        <div className="px-5">
          {items.map((item) => (
            <NoteCard
              key={item.id}
              item={item}
              onOpen={(note) => {
                navigate(`/notes/${note.id}`);
              }}
              onEdit={(note) => setEditingNote(note)}
              onDelete={(id) => deleteMut.mutate(id)}
            />
          ))}
        </div>
      )}

      {/* 编辑笔记弹窗 */}
      {editingNote && (
        <NoteForm
          note={editingNote}
          onSubmit={(content) => {
            updateMut.mutate({ id: editingNote.id, content });
          }}
          onCancel={() => setEditingNote(null)}
          isSubmitting={updateMut.isPending}
        />
      )}
    </div>
  );
}

// ========== 笔记卡片 ==========

function NotesHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header className="sticky top-0 z-20 bg-white">
      <div className="relative flex h-16 items-center justify-center px-4">
        <button
          type="button"
          onClick={onBack}
          className="absolute left-3 flex h-10 w-10 items-center justify-center text-gray-900"
          aria-label="返回"
        >
          <ChevronLeftIcon />
        </button>
        <h1 className="text-[20px] font-semibold text-gray-950">{title}</h1>
        <div className="absolute right-3 flex h-10 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-900">
          <span className="text-lg leading-none">•••</span>
          <span className="mx-3 h-5 w-px bg-gray-200" />
          <span className="h-5 w-5 rounded-full border-[5px] border-gray-900" />
        </div>
      </div>
    </header>
  );
}

function NoteCard({
  item,
  onOpen,
  onEdit,
  onDelete,
}: {
  item: NoteDTO;
  onOpen: (note: NoteDTO) => void;
  onEdit: (note: NoteDTO) => void;
  onDelete: (id: string) => void;
}) {
  const q = item.question;

  return (
    <article className="border-b border-gray-100 py-5">
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="block w-full text-left"
      >
        <p className="text-[22px] leading-7 font-medium text-gray-400">{formatDateTime(item.updated_at)}</p>
        <h2 className="mt-4 text-[24px] leading-8 font-semibold text-gray-950 whitespace-pre-wrap">
          {item.content}
        </h2>

        <div className="mt-3 rounded-xl bg-[#f4f4f6] px-6 py-6">
          <p className="text-center text-[20px] leading-9 font-semibold text-gray-900">
            <span className="mr-2 inline-flex translate-y-[-2px] rounded-md bg-[#6653ff] px-2 py-1 text-[16px] leading-none text-white">
              {getTypeLabel(q?.type)}
            </span>
            {q?.content ?? "关联题目已不可用"}
          </p>
        </div>
      </button>

      <div className="mt-3 flex items-center justify-end gap-4 text-sm">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(item);
          }}
          className="text-gray-400 hover:text-blue-500 transition-colors"
        >
          编辑
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(item.id);
          }}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          删除
        </button>
      </div>
    </article>
  );
}

// ========== 笔记表单弹窗 ==========

function NoteForm({
  note,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  note: NoteDTO | null;
  onSubmit: (content: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [content, setContent] = useState(note?.content ?? "");

  const handleSubmit = () => {
    if (!content.trim()) return;
    onSubmit(content.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center">
      <div className="bg-white w-full max-w-lg rounded-t-2xl p-4 max-h-[70vh] flex flex-col">
        <h3 className="text-lg font-semibold mb-3">
          {note ? "编辑笔记" : "新建笔记"}
        </h3>
        <textarea
          autoFocus
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="写下你的笔记..."
          className="flex-1 min-h-[120px] p-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <div className="flex gap-3 mt-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            disabled={!content.trim() || isSubmitting}
            onClick={handleSubmit}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white font-medium hover:bg-blue-600 transition-colors disabled:opacity-40"
          >
            {isSubmitting ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(ts: number): string {
  const d = new Date(ts);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function getTypeLabel(type?: string): string {
  const map: Record<string, string> = {
    single_choice: "单选",
    multiple_choice: "多选",
    true_false: "判断",
    fill_blank: "填空",
    essay: "简答",
  };
  return type ? map[type] ?? type : "题目";
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
